import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertCircle, Info, AlertTriangle, Check, CheckCheck, Filter, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Zentrale Notifications-Seite
 * 
 * Vollständige Liste aller Benachrichtigungen mit:
 * - Filter (Status, Severity, Modul)
 * - Aktionen (Als gelesen, Als erledigt)
 * - Pagination
 * - Deep-Link Navigation
 */
export const NotificationsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all', // all, unread, read, done
    severity: 'all', // all, info, warning, critical
    source_module: 'all' // all, MHD, Temperatur, etc.
  });
  const [pagination, setPagination] = useState({
    limit: 20,
    skip: 0,
    total: 0
  });

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pagination.limit,
        skip: pagination.skip
      };
      
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.severity !== 'all') params.severity = filters.severity;
      if (filters.source_module !== 'all') params.source_module = filters.source_module;
      
      const response = await axios.get(`${API}/api/notifications`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(response.data.notifications);
      setPagination(prev => ({ ...prev, total: response.data.count }));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchNotifications();
    }
  }, [token, filters, pagination.skip]);

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(
        `${API}/api/notifications/${notificationId}/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, status: 'read' } : n)
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Mark as done
  const markAsDone = async (notificationId) => {
    try {
      await axios.patch(
        `${API}/api/notifications/${notificationId}/mark-done`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, status: 'done' } : n)
      );
    } catch (error) {
      console.error('Error marking as done:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.post(
        `${API}/api/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev =>
        prev.map(n => n.status === 'unread' ? { ...n, status: 'read' } : n)
      );
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Handle notification click (navigate to deep-link)
  const handleNotificationClick = (notification) => {
    // Mark as read if unread
    if (notification.status === 'unread') {
      markAsRead(notification.id);
    }

    // Navigate to deep-link or fallback
    if (notification.link_to) {
      navigate(notification.link_to);
    }
  };

  // Get severity icon
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  // Get severity badge color
  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'warning':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      case 'info':
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'done':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'read':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      case 'unread':
      default:
        return 'bg-primary/10 text-primary font-semibold';
    }
  };

  // Format time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück
        </Button>
        
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/10 rounded-lg">
            <Bell className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Benachrichtigungen</h1>
            <p className="text-sm text-muted-foreground">
              Alle Benachrichtigungen im Überblick
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Filter</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Status Filter */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            >
              <option value="all">Alle</option>
              <option value="unread">Ungelesen</option>
              <option value="read">Gelesen</option>
              <option value="done">Erledigt</option>
            </select>
          </div>

          {/* Severity Filter */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">
              Schweregrad
            </label>
            <select
              value={filters.severity}
              onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            >
              <option value="all">Alle</option>
              <option value="info">Info</option>
              <option value="warning">Warnung</option>
              <option value="critical">Kritisch</option>
            </select>
          </div>

          {/* Module Filter */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">
              Modul
            </label>
            <select
              value={filters.source_module}
              onChange={(e) => setFilters(prev => ({ ...prev, source_module: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            >
              <option value="all">Alle</option>
              <option value="MHD">MHD</option>
              <option value="Temperatur">Temperatur</option>
              <option value="Aufgaben">Aufgaben</option>
              <option value="System">System</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            {notifications.length} Benachrichtigung{notifications.length !== 1 ? 'en' : ''}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={markAllAsRead}
            disabled={!notifications.some(n => n.status === 'unread')}
          >
            <CheckCheck className="h-4 w-4 mr-2" />
            Alle als gelesen
          </Button>
        </div>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground mt-4">Lädt...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Bell className="h-12 w-12 text-muted mx-auto mb-4 opacity-50" />
          <p className="text-muted-foreground">Keine Benachrichtigungen</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow ${
                notification.link_to ? 'cursor-pointer' : ''
              }`}
              onClick={() => notification.link_to && handleNotificationClick(notification)}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 mt-1">
                  {getSeverityIcon(notification.severity)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className={`text-sm font-medium text-foreground ${
                      notification.status === 'unread' ? 'font-semibold' : ''
                    }`}>
                      {notification.title}
                    </h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs px-2 py-1 rounded-full ${getSeverityBadge(notification.severity)}`}>
                        {notification.severity === 'critical' ? 'Kritisch' :
                         notification.severity === 'warning' ? 'Warnung' : 'Info'}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(notification.status)}`}>
                        {notification.status === 'done' ? 'Erledigt' :
                         notification.status === 'read' ? 'Gelesen' : 'Ungelesen'}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">
                    {notification.message}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{formatTime(notification.created_at)}</span>
                      {notification.source_module && (
                        <>
                          <span>•</span>
                          <span className="font-medium">{notification.source_module}</span>
                        </>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {notification.status === 'unread' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Als gelesen
                        </Button>
                      )}
                      {notification.status !== 'done' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsDone(notification.id);
                          }}
                        >
                          <CheckCheck className="h-3 w-3 mr-1" />
                          Erledigt
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Simple Pagination */}
      {notifications.length > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
            disabled={pagination.skip === 0}
          >
            Zurück
          </Button>
          <span className="text-sm text-muted-foreground">
            {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)}
          </span>
          <Button
            variant="outline"
            onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
            disabled={pagination.skip + pagination.limit >= pagination.total}
          >
            Weiter
          </Button>
        </div>
      )}
    </div>
  );
};
